import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  signal,
  effect,
} from '@angular/core';

import moment from 'moment';
import 'moment/locale/ru';

export interface CalendarDate {
  mDate: moment.Moment;
  today?: boolean;
}

@Component({
  selector: 'ux-calendar',
  templateUrl: './calendar.component.html',
  styleUrls: ['./calendar.component.less'],
  standalone: true,
  imports: [CommonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent {
  protected readonly currentDate$ = signal<moment.Moment>(moment());
  protected readonly selectedDate$ = signal<moment.Moment | null>(null);
  protected readonly dayNames = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

  protected readonly weeks = computed(() => {
    const dates = this.fillDates(this.currentDate$());
    const weeks: CalendarDate[][] = [];
    while (dates.length > 0) {
      weeks.push(dates.splice(0, 7));
    }
    return weeks;
  });

  protected readonly currentMonthYear = computed(() => ({
    month: this.currentDate$().format('MMMM'),
    year: this.currentDate$().format('YYYY'),
  }));

  @Output() changeDate = new EventEmitter<CalendarDate>();
  @Output() clearDate = new EventEmitter<void>();

  // TO DO (bad types)
  @Input()
  set selectedDate(date: moment.Moment | string | null) {
    this.selectedDate$.set(date as moment.Moment);
  }

  constructor() {
    moment.locale('ru');
    effect(() => {
      // Автоматически обновляем календарь при изменении текущей даты
      this.currentDate$();
      this.weeks();
    });
  }

  protected isToday(date: moment.Moment): boolean {
    return moment().isSame(moment(date), 'day');
  }

  protected isSelected(date: moment.Moment): boolean {
    const selectedDate = this.selectedDate$();
    return selectedDate ? moment(date).isSame(selectedDate, 'day') : false;
  }

  protected isSelectedMonth(date: moment.Moment): boolean {
    return moment(date).isSame(this.currentDate$(), 'month');
  }

  protected isWeekend(date: moment.Moment): boolean {
    const dayOfWeek = date.weekday();
    return dayOfWeek === 5 || dayOfWeek === 6;
  }

  protected selectDate(date: CalendarDate): void {
    this.selectedDate$.set(date.mDate);
    this.changeDate.emit(date);
  }

  protected prevMonth(): void {
    this.currentDate$.update(date => moment(date).subtract(1, 'months'));
  }

  protected nextMonth(): void {
    this.currentDate$.update(date => moment(date).add(1, 'months'));
  }

  protected firstMonth(): void {
    this.currentDate$.update(date => moment(date).startOf('year'));
  }

  protected lastMonth(): void {
    this.currentDate$.update(date => moment(date).endOf('year'));
  }

  protected prevYear(): void {
    this.currentDate$.update(date => moment(date).subtract(1, 'year'));
  }

  protected nextYear(): void {
    this.currentDate$.update(date => moment(date).add(1, 'year'));
  }

  protected onClearDay(): void {
    this.selectedDate$.set(null);
    this.clearDate.emit();
  }

  private fillDates(currentMoment: moment.Moment): CalendarDate[] {
    const firstOfMonth = moment(currentMoment).startOf('month').weekday();
    const firstDayOfGrid = moment(currentMoment).startOf('month').subtract(firstOfMonth, 'days');

    const dates: CalendarDate[] = [];
    for (let i = 0; i < 42; i++) {
      const d = moment(firstDayOfGrid).add(i, 'days');
      dates.push({
        today: this.isToday(d),
        mDate: d,
      });
    }
    return dates;
  }
}
